import {
  Action,
  Badge,
  Card,
  ConfirmModal,
  Icon,
  Page,
  Disclosure,
  SimpleSummary,
} from 'corso-admin-ui';
import { CrewMerchantApi } from 'corso-types';
import { useState } from 'react';
import DescriptionList from '~/components/DescriptionList';
import EmptyStateAction from '~/components/EmptyStateAction';
import {
  useDiscountDefinitionDelete,
  useDiscountDefinitions,
} from '~/features/settings/hooks/useDiscountDefinitions';
import { useCurrency } from '~/hooks/useCurrency';
import { DiscountDefinitionSettingsForm } from './DiscountDefinitionSettingsForm';
import {
  buildDiscountDescriptions,
  discountKindLabel,
  formatDiscountValue,
} from './discountSummary';

// -- discount kind picker metadata (icons + description for the prestep modal) --

// -- components --

type DiscountDefinition = CrewMerchantApi.ResponseBody<
  '/:storeId/discount-definition',
  'get'
>[number];

function DeleteDiscountDefinition({
  definition,
}: {
  definition: DiscountDefinition;
}) {
  const { mutate: deleteDiscountDefinition, isPending } =
    useDiscountDefinitionDelete();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const confirmDelete = () => {
    deleteDiscountDefinition(definition.id);
    setShowConfirmation(false);
  };

  return (
    <>
      <Action
        icon="remove"
        variant="ghost"
        accessibilityLabel={`Delete ${definition.title}`}
        loading={isPending}
        onClick={() => setShowConfirmation(true)}
      />

      <ConfirmModal
        title="Delete Discount"
        prompt={`Are you sure you want to delete ${definition.title}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        show={showConfirmation}
        loading={isPending}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
}

function DiscountDefinitionDisplay({
  definition,
}: {
  definition: DiscountDefinition;
}) {
  const { currencyCode } = useCurrency();
  const { title, discountDefinition } = definition;
  const { parameters } = discountDefinition;

  const descriptions = buildDiscountDescriptions(definition, currencyCode);

  return (
    <Disclosure
      renderSummary={
        <SimpleSummary>
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="flex flex-col items-start gap-1">
              <p className="truncate text-left text-sm font-medium text-balance text-neutral-700">
                {title}
              </p>
              <p className="text-xs text-neutral-500">
                {discountKindLabel(parameters.kind)}
              </p>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Badge>
                {formatDiscountValue(
                  parameters.customerGets.value,
                  currencyCode,
                )}
              </Badge>
              <DeleteDiscountDefinition definition={definition} />
            </div>
          </div>
        </SimpleSummary>
      }
    >
      <div className="mt-1 px-2">
        <DescriptionList descriptions={descriptions} />
      </div>
    </Disclosure>
  );
}

export default function DiscountDefinitionSettingsPage() {
  const { data: definitions, isLoading } = useDiscountDefinitions();
  const [showCreate, setShowCreate] = useState(false);

  const hasDefinitions = definitions.length > 0 && !isLoading;

  return (
    <Page
      title="Discounts"
      primaryAction={{
        onAction: () => setShowCreate(true),
        icon: 'add',
        content: 'Add Discount',
      }}
    >
      <Card>
        {hasDefinitions ? (
          <ul className="flex flex-col gap-2">
            {definitions.map((definition) => (
              <li key={definition.id}>
                <DiscountDefinitionDisplay definition={definition} />
              </li>
            ))}
          </ul>
        ) : (
          <EmptyStateAction.Button
            onClick={() => setShowCreate(true)}
            icon={<Icon icon="discount" />}
            label="Create a discount"
            description="Discounts define reusable templates that can be used to generate discount codes."
          />
        )}

        <DiscountDefinitionSettingsForm
          onClose={() => setShowCreate(false)}
          show={showCreate}
        />
      </Card>
    </Page>
  );
}
